import {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TaskEventDateIcon } from '@/components/ui/TaskEventDateIcon';

type LucideIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;

export interface DatePickerProps {
  label?: string;
  value: string; // ISO date string "YYYY-MM-DD" or ""
  onChange: (date: string) => void;
  placeholder?: string;
  error?: boolean;
  labelIcon?: ReactNode;
  /** Trigger icon on the right side of the input. Defaults to Calendar. */
  icon?: LucideIcon;
  /**
   * `YYYY-MM-DD` whose month the calendar opens on while there is no value
   * (e.g. a series' first session for its end date). Defaults to today.
   */
  defaultMonth?: string;
  /**
   * Floating variant: the calendar is portaled to <body> with fixed
   * positioning, so an ancestor with overflow hidden (a Review card) can't
   * clip it. Off by default — the calendar then stays an absolute dropdown
   * inside the picker, exactly as before.
   */
  floating?: boolean;
}

const PANEL_WIDTH = 280;
const PANEL_GAP = 4;
const VIEWPORT_MARGIN = 8;

/**
 * Where the calendar goes, in viewport coordinates: under the trigger, or
 * above it when there is no room below; kept inside the viewport sideways.
 */
function panelPosition(
  trigger: DOMRect,
  panelHeight: number,
): { top: number; left: number } {
  const below = trigger.bottom + PANEL_GAP;
  const above = trigger.top - PANEL_GAP - panelHeight;
  const fitsBelow = below + panelHeight <= window.innerHeight - VIEWPORT_MARGIN;
  const top = fitsBelow || above < VIEWPORT_MARGIN ? below : above;
  const maxLeft = window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN;
  const left = Math.max(VIEWPORT_MARGIN, Math.min(trigger.left, maxLeft));
  return { top, left };
}

/* ── Calendar helpers ── */

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay(); // 0 = Sunday
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function formatDisplayDate(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  // Per design spec: weekday, month and day (no year).
  // Example: "Monday, June 2"
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function toIso(year: number, month: number, day: number): string {
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

function todayIso(): string {
  const now = new Date();
  return toIso(now.getFullYear(), now.getMonth(), now.getDate());
}

/* ── Component ── */

export const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      label,
      value,
      onChange,
      placeholder = 'Select date',
      error = false,
      labelIcon,
      icon: Icon = TaskEventDateIcon,
      defaultMonth,
      floating = false,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);

    // Calendar view state: which month is displayed
    const [viewYear, setViewYear] = useState(() => {
      const seed = value || defaultMonth;
      if (seed) {
        const [y] = seed.split('-').map(Number);
        return y;
      }
      return new Date().getFullYear();
    });
    const [viewMonth, setViewMonth] = useState(() => {
      const seed = value || defaultMonth;
      if (seed) {
        const parts = seed.split('-').map(Number);
        return parts[1] - 1;
      }
      return new Date().getMonth();
    });

    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [panelPos, setPanelPos] = useState<{
      top: number;
      left: number;
    } | null>(null);

    /*
     * Floating variant only: the calendar is portaled to <body> with fixed
     * positioning, so an ancestor with overflow hidden (a Review card) can't
     * clip it. Its position follows the trigger while open: on scroll
     * anywhere (capture) and on resize.
     */
    useLayoutEffect(() => {
      if (!open || !floating) {
        setPanelPos(null);
        return;
      }
      const place = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const height = panelRef.current?.offsetHeight ?? 0;
        setPanelPos(panelPosition(trigger.getBoundingClientRect(), height));
      };
      place();
      window.addEventListener('scroll', place, true);
      window.addEventListener('resize', place);
      return () => {
        window.removeEventListener('scroll', place, true);
        window.removeEventListener('resize', place);
      };
    }, [open, floating]);

    /* Re-place once the panel has a height (first paint measured 0). */
    useLayoutEffect(() => {
      if (!open || !floating || !panelRef.current || !triggerRef.current) {
        return;
      }
      setPanelPos(
        panelPosition(
          triggerRef.current.getBoundingClientRect(),
          panelRef.current.offsetHeight,
        ),
      );
    }, [open, floating, viewYear, viewMonth]);

    /* Floating: portaled to <body>. Default: rendered in place, as before. */
    const renderPanel = (panel: ReactNode) =>
      floating ? createPortal(panel, document.body) : panel;

    /* Sync view to value when value changes externally */
    useEffect(() => {
      if (value) {
        const [y, m] = value.split('-').map(Number);
        setViewYear(y);
        setViewMonth(m - 1);
      }
    }, [value]);

    /* Close on outside click */
    useEffect(() => {
      function handleClickOutside(e: MouseEvent) {
        const target = e.target as Node;
        if (
          containerRef.current &&
          !containerRef.current.contains(target) &&
          !panelRef.current?.contains(target)
        ) {
          setOpen(false);
        }
      }
      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    /* Navigation */
    const prevMonth = useCallback(() => {
      setViewMonth((m) => {
        if (m === 0) {
          setViewYear((y) => y - 1);
          return 11;
        }
        return m - 1;
      });
    }, []);

    const nextMonth = useCallback(() => {
      setViewMonth((m) => {
        if (m === 11) {
          setViewYear((y) => y + 1);
          return 0;
        }
        return m + 1;
      });
    }, []);

    /* Build grid */
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const today = todayIso();
    const selectedIso = value || '';

    // Previous month trailing days
    const prevMonthDays = getDaysInMonth(
      viewMonth === 0 ? viewYear - 1 : viewYear,
      viewMonth === 0 ? 11 : viewMonth - 1,
    );

    const cells: Array<{
      day: number;
      type: 'prev' | 'current' | 'next';
      iso: string;
    }> = [];

    // Leading days from previous month
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = prevMonthDays - i;
      const pm = viewMonth === 0 ? 11 : viewMonth - 1;
      const py = viewMonth === 0 ? viewYear - 1 : viewYear;
      cells.push({ day: d, type: 'prev', iso: toIso(py, pm, d) });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({
        day: d,
        type: 'current',
        iso: toIso(viewYear, viewMonth, d),
      });
    }

    // Trailing days from next month to fill last row
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      const nm = viewMonth === 11 ? 0 : viewMonth + 1;
      const ny = viewMonth === 11 ? viewYear + 1 : viewYear;
      for (let d = 1; d <= remaining; d++) {
        cells.push({ day: d, type: 'next', iso: toIso(ny, nm, d) });
      }
    }

    return (
      <div
        ref={(node) => {
          // Merge refs
          (
            containerRef as React.MutableRefObject<HTMLDivElement | null>
          ).current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref)
            (ref as React.MutableRefObject<HTMLDivElement | null>).current =
              node;
        }}
        className="relative flex flex-col gap-1.5 w-full"
      >
        {/* Label */}
        {label && (
          <label className="text-sm font-medium text-foreground leading-5 flex items-center gap-1.5">
            {labelIcon}
            {label}
          </label>
        )}

        {/* Input trigger */}
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={[
            'w-full h-10 rounded-md border bg-background text-sm leading-5',
            'px-3 py-2',
            'flex items-center gap-2 cursor-pointer',
            'transition-colors duration-150',
            'focus:outline-2 focus:outline-offset-0 focus:outline-border-focus focus:border-border-focus',
            error ? 'border-border-error' : 'border-border',
            value ? 'text-foreground' : 'text-muted-foreground',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span className="flex-1 text-left truncate">
            {value ? formatDisplayDate(value) : placeholder}
          </span>
          <Icon
            size={20}
            className="shrink-0 text-icon-default"
          />
        </button>

        {/* Calendar dropdown */}
        {open &&
          renderPanel(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Date picker calendar"
              className={[
                floating
                  ? 'fixed z-[200]'
                  : 'absolute top-full left-0 mt-1 z-[200]',
                'w-[280px] bg-background border border-border rounded-lg shadow-md',
                'p-3',
              ].join(' ')}
              style={
                floating
                  ? {
                      top: panelPos?.top ?? 0,
                      left: panelPos?.left ?? 0,
                      // Hidden until placed, so it never flashes at the corner.
                      visibility: panelPos ? 'visible' : 'hidden',
                    }
                  : undefined
              }
            >
              {/* Month/year header with navigation */}
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  onClick={prevMonth}
                  className="inline-flex items-center justify-center rounded-md p-1 text-foreground hover:bg-surface-secondary transition-colors duration-150 cursor-pointer"
                  aria-label="Previous month"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-sm font-semibold text-foreground">
                  {MONTH_NAMES[viewMonth]} {viewYear}
                </span>
                <button
                  type="button"
                  onClick={nextMonth}
                  className="inline-flex items-center justify-center rounded-md p-1 text-foreground hover:bg-surface-secondary transition-colors duration-150 cursor-pointer"
                  aria-label="Next month"
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              {/* Day headers */}
              <div
                className="grid grid-cols-7 mb-1"
                role="row"
              >
                {DAY_HEADERS.map((d) => (
                  <div
                    key={d}
                    className="text-center text-xs font-medium text-muted-foreground py-1"
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* Day grid */}
              <div
                className="grid grid-cols-7"
                role="grid"
                aria-label={`${MONTH_NAMES[viewMonth]} ${viewYear}`}
              >
                {cells.map((cell, idx) => {
                  const isSelected = cell.iso === selectedIso;
                  const isToday = cell.iso === today;
                  const isOutside = cell.type !== 'current';

                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        if (isOutside) {
                          // Navigate to that month and select
                          if (cell.type === 'prev') prevMonth();
                          else nextMonth();
                        }
                        onChange(cell.iso);
                        setOpen(false);
                      }}
                      className={[
                        'w-9 h-9 rounded-full text-sm flex items-center justify-center',
                        'transition-colors duration-150 cursor-pointer',
                        isSelected
                          ? 'bg-primary text-primary-foreground font-semibold'
                          : isToday && !isOutside
                            ? 'font-semibold text-primary'
                            : isOutside
                              ? 'text-muted-foreground'
                              : 'text-foreground hover:bg-surface-secondary',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-label={cell.iso}
                      aria-pressed={isSelected}
                    >
                      {cell.day}
                    </button>
                  );
                })}
              </div>
            </div>,
          )}
      </div>
    );
  },
);

DatePicker.displayName = 'DatePicker';
